import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../Api';
import { InsuranceCompanyDTO } from '../types/InsuranceCompany';
import { FormSelect } from '../controls/FormSelect';
import CustomInput from '../controls/CustomInput';

const InsuranceCompanyFormPage: React.FC = () => {
  const { familyId } = useParams<{ familyId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApi();

  const [formData, setFormData] = useState<InsuranceCompanyDTO>({
    id: 0,
    businessName: '',
    insuranceType: '',
    businessWebsite: '',
    businessEmail: '',
    businessPhone: '',
    businessLocationAddressLine1: '',
    businessLocationAddressLine2: '',
    businessLocationAddressCity: '',
    businessLocationAddressState: '',
    businessLocationAddressZipCode: '',
    businessLocationAddressCountry: '',
    contactNotes: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const loadInsuranceCompany = async (insuranceCompanyId: number) => {
      try {
        setIsLoading(true);
        const response = await api.get(`/api/insurancecompany/getinsurancecompany?id=${insuranceCompanyId}`);
        setFormData(response.data.insuranceCompany);
      } catch (error) {
        console.error('Error loading Insurance Company:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const insuranceCompanyId = new URLSearchParams(location.search).get('insuranceCompanyId');
    if (insuranceCompanyId) {
      setIsEditing(true);
      loadInsuranceCompany(parseInt(insuranceCompanyId));
    }
  }, [location.search, api]);

  const handleFieldChange = (field: string) => (value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!familyId) return;

    try {
      setIsLoading(true);

      if (isEditing) {
        await api.put(`/api/insurancecompany/updateinsurancecompany`, formData);
      } else {
        await api.post(`/api/insurancecompany/createinsurancecompany?familyId=${familyId}`, formData);
      }
      navigate(`/family-management/${familyId}`);
    } catch (error) {
      console.error('Error saving insurance company:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (familyId) {
      navigate(`/family-management/${familyId}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Header Card */}
          <div className="bg-white rounded-xl shadow-md">
            <div className="px-6 py-8">
              <h1 className="text-3xl font-bold mb-2">
                {isEditing ? 'Edit Insurance Company' : 'Add New Insurance Company'}
              </h1>
              <p className="text-lg">
                {isEditing ? 'Update Insurance Company information' : 'Enter Insurance Company information to add to the family'}
              </p>
            </div>
          </div>

          {/* Basic Information Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <CustomInput
                  label="Business Name"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleFieldChange('businessName')}
                  required
                />
              </div>
              <div>
                <FormSelect
                  label="Insurance Type"
                  value={formData.insuranceType || null}
                  options={[
                    { value: 'Type 1', label: 'Type 1' },
                    { value: 'Type 2', label: 'Type 2' },
                  ]}
                  onChange={(val) => setFormData(prev => ({ ...prev, insuranceType: (val as string) ?? '' }))}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Business Website"
                  name="businessWebsite"
                  value={formData.businessWebsite}
                  onChange={handleFieldChange('businessWebsite')}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Business Email"
                  name="businessEmail"
                  type="email"
                  value={formData.businessEmail}
                  onChange={handleFieldChange('businessEmail')}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Business Phone"
                  name="businessPhone"
                  type="tel"
                  value={formData.businessPhone ?? ''}
                  onChange={handleFieldChange('businessPhone')}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Address Line 1"
                  name="businessLocationAddressLine1"
                  value={formData.businessLocationAddressLine1}
                  onChange={handleFieldChange('businessLocationAddressLine1')}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Address Line 2"
                  name="businessLocationAddressLine2"
                  value={formData.businessLocationAddressLine2 ?? ''}
                  onChange={handleFieldChange('businessLocationAddressLine2')}
                />
              </div>
              <div>
                <CustomInput
                  label="City"
                  name="businessLocationAddressCity"
                  value={formData.businessLocationAddressCity}
                  onChange={handleFieldChange('businessLocationAddressCity')}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="State"
                  name="businessLocationAddressState"
                  value={formData.businessLocationAddressState}
                  onChange={handleFieldChange('businessLocationAddressState')}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Zip Code"
                  name="businessLocationAddressZipCode"
                  value={formData.businessLocationAddressZipCode}
                  onChange={handleFieldChange('businessLocationAddressZipCode')}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Country"
                  name="businessLocationAddressCountry"
                  value={formData.businessLocationAddressCountry}
                  onChange={handleFieldChange('businessLocationAddressCountry')}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Contact Notes"
                  name="contactNotes"
                  value={formData.contactNotes ?? ''}
                  onChange={handleFieldChange('contactNotes')}
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleCancel}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary"
            >
              {isLoading ? 'Saving...' : (isEditing ? 'Update Insurance Company' : 'Add Insurance Company')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default InsuranceCompanyFormPage;
